import { ref, onUnmounted } from 'vue';

export type ResizeAxis = 'horizontal' | 'vertical';

// which edge the handle sits on — determines drag direction sign
// 'end' means handle is at the right/bottom edge (drag right = grow)
// 'start' means handle is at the left/top edge (drag right = shrink)
export type ResizeEdge = 'start' | 'end';

export type UseResizeOptions = {
	axis: ResizeAxis;
	edge: ResizeEdge;
	initial: number;
	min?: number;
	max?: number;
};

export function useResize(options: UseResizeOptions) {
	const size = ref(options.initial);
	const dragging = ref(false);

	let startPos = 0;
	let startSize = 0;

	function onPointerDown(e: PointerEvent) {
		e.preventDefault();
		dragging.value = true;
		startPos = options.axis === 'horizontal' ? e.clientX : e.clientY;
		startSize = size.value;

		document.addEventListener('pointermove', onPointerMove);
		document.addEventListener('pointerup', onPointerUp);
		// prevent text selection during drag
		document.body.style.userSelect = 'none';
		document.body.style.cursor = options.axis === 'horizontal' ? 'col-resize' : 'row-resize';
	}

	function onPointerMove(e: PointerEvent) {
		const currentPos = options.axis === 'horizontal' ? e.clientX : e.clientY;
		const delta = currentPos - startPos;
		// 'end' edge: dragging in positive direction grows the panel
		// 'start' edge: dragging in positive direction shrinks the panel
		const sign = options.edge === 'end' ? 1 : -1;
		let newSize = startSize + delta * sign;

		if (options.min !== undefined) newSize = Math.max(options.min, newSize);
		if (options.max !== undefined) newSize = Math.min(options.max, newSize);

		size.value = newSize;
	}

	function onPointerUp() {
		dragging.value = false;
		document.removeEventListener('pointermove', onPointerMove);
		document.removeEventListener('pointerup', onPointerUp);
		document.body.style.userSelect = '';
		document.body.style.cursor = '';
	}

	onUnmounted(() => {
		// cleanup in case component unmounts mid-drag
		document.removeEventListener('pointermove', onPointerMove);
		document.removeEventListener('pointerup', onPointerUp);
		document.body.style.userSelect = '';
		document.body.style.cursor = '';
	});

	return {
		size,
		dragging,
		onPointerDown,
	};
}
